import { inject } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { MatDialog } from "@angular/material/dialog";
import { ComponentStore } from "@ngrx/component-store";
import { tapResponse } from "@ngrx/operators";
import { filter, mergeMap, pipe, switchMap, takeUntil, tap, withLatestFrom } from "rxjs";
import {
    Approval,
    ApprovalSort,
    ApprovalSortBy,
    EApprovalStatus,
    EnumApprovalsTabs,
    SortDirection,
} from 'src/app/models/approval.model'; import { ApprovalApiService, UpdateExpenseApprovalFields } from "src/app/services/approvals-api.service";
import { PageResult } from "src/app/shared/models/pageResult";
import { SelectedFilter } from "src/app/shared/models/filter.model";
import { AuthStore } from "src/app/store/auth-store";
import { GlobalStore } from "src/app/store/global-store";
import { ManipulationsToolbarStore } from "src/app/store/manipulations-toolbar-store";
import { SendSuccessComponent } from "../../create-questionnaire-page/component/send-success/send-success.component";
import { Router } from "@angular/router";
import { TostesStore } from "src/app/store/tostes-store";
import { TostPositionEnum, TostTypeEnum } from "src/app/shared/models/tostes.model";
import { HttpErrorResponse } from "@angular/common/http";
import { Expense } from "src/app/models/Answer.model";


export interface ApprovalState {
    approvals: PageResult<Approval>;
    approvalsExtra: PageResult<Approval>;
    readonly loading: boolean;
    readonly loadingExtra: boolean;
    isRequestOfExtaraWasEmpty: boolean;
    error?: string;
    // Collapsible approvals screen: which submissions are expanded/selected, the expense rows loaded
    // per submission (lazy, cached until the list reloads), and which submissions are mid-fetch.
    expandedIds: Record<number, boolean>;
    selectedIds: Record<number, boolean>;
    expenseRowsByApprovalId: Record<number, Expense[]>;
    loadingExpenseRowsIds: Record<number, boolean>;
    expenseRowsErrorByApprovalId: Record<number, string>;
    // Sender-name hover tooltip: total approved amount per sender, keyed by "senderId:filterJson" so
    // every row for the same sender (there's often more than one) shares one fetch instead of each
    // row hitting the server independently on its own hover.
    senderApprovedTotals: Record<string, number>;
    loadingSenderApprovedTotalKeys: Record<string, boolean>;
    // Approval ids that currently have an expense row showing an unsaved, over-the-amount
    // approvedAmount (the row refuses to save it, so it never reaches the server) - tracked here,
    // not just locally on the row, so bulkApprove can refuse to approve a submission that still has
    // one of these unresolved instead of silently approving on the last valid saved amount.
    invalidApprovalIds: Record<number, boolean>;
}

export class ApprovalStore extends ComponentStore<ApprovalState> {
    #approvalSercive = inject(ApprovalApiService);
    #manipulationsToolbarStore = inject(ManipulationsToolbarStore)
    #authStore = inject(AuthStore)
    #globalStore = inject(GlobalStore)
    #dialog = inject(MatDialog);
    #router = inject(Router);
    #tostesStore = inject(TostesStore);

    readonly approvals$ = this.select((x) => x.approvals)
    readonly approvalsExtra$ = this.select((x) => x.approvalsExtra)
    readonly loading$ = this.select((x) => x.loading);
    readonly loadingExtra$ = this.select((x) => x.loadingExtra);
    readonly isEmpty$ = this.select((x) => x.approvals.isEmpty)
    readonly totals$ = this.select((x) => x.approvals.totals)
    readonly expandedIds$ = this.select((x) => x.expandedIds)
    readonly expenseRowsByApprovalId$ = this.select((x) => x.expenseRowsByApprovalId)
    readonly loadingExpenseRowsIds$ = this.select((x) => x.loadingExpenseRowsIds)
    readonly expenseRowsErrorByApprovalId$ = this.select((x) => x.expenseRowsErrorByApprovalId)
    readonly selectedIds$ = this.select((x) => x.selectedIds)
    readonly selectedCount$ = this.select(this.selectedIds$, (selectedIds) => Object.values(selectedIds).filter(Boolean).length)
    readonly senderApprovedTotals$ = this.select((x) => x.senderApprovedTotals)
    readonly invalidApprovalIds$ = this.select((x) => x.invalidApprovalIds)
    readonly hasInvalidSelectedApproval$ = this.select(
        this.selectedIds$,
        this.invalidApprovalIds$,
        (selectedIds, invalidApprovalIds) =>
            Object.keys(selectedIds).some((id) => selectedIds[+id] && invalidApprovalIds[+id])
    )
    private currentSort: ApprovalSort = {
        sortBy: ApprovalSortBy.SenderName,
        sortDirection: SortDirection.Asc,
    };
    loggesUser$ = toSignal(this.#authStore.loggedUserDetails$)
    approvalsExtraSig$ = toSignal(this.approvalsExtra$)
    approvalsSig$ = toSignal(this.approvals$)
    loadingExtraSig$ = toSignal(this.loadingExtra$)

    constructor() {
        super({
            approvals: { result: [], totals: 0 },
            approvalsExtra: { result: [], totals: 0 },
            loading: false,
            loadingExtra: false,
            isRequestOfExtaraWasEmpty: false,
            expandedIds: {},
            selectedIds: {},
            expenseRowsByApprovalId: {},
            loadingExpenseRowsIds: {},
            expenseRowsErrorByApprovalId: {},
            senderApprovedTotals: {},
            loadingSenderApprovedTotalKeys: {},
            invalidApprovalIds: {}
        });

        // Don't load approvals before a tab is actually known - at construction time selectedFilter$
        // replays the store's initial, tab-less filter (ApprovalsComponent.ngOnInit sets the real
        // one right after), and loading against that placeholder would just be a redundant
        // getApprovals/getExtraApprovals round trip that's immediately superseded. Also skip the
        // Reports tab entirely - it's rendered by <que-reports-list>/ReportStore instead, so firing
        // getApprovals for it would just be a wasted request the server doesn't expect.
        this.#manipulationsToolbarStore.selectedFilter$
            .pipe(
                filter((selectedFilter) => selectedFilter?.approvalsTabs !== undefined
                    && selectedFilter?.approvalsTabs !== EnumApprovalsTabs.Reports),
                takeUntil(this.destroy$))
            .subscribe(x => {
                this.getApprovals()
            });

    }
    changeSort(sort: ApprovalSort): void {
        this.currentSort = sort;

        this.patchState({
            approvals: {
                result: [],
                totals: 0,
            },
            approvalsExtra: {
                result: [],
                totals: 0,
            },
            isRequestOfExtaraWasEmpty: false,
        });

        this.getApprovals(true);
    }
    readonly getApprovals = this.effect<void | boolean>(
        pipe(
            tap(() => this.patchState({ loading: true })),
            tap((ev) => {
                if (ev) {
                    this.#manipulationsToolbarStore.resetPage();
                }
            }),
            withLatestFrom(this.#manipulationsToolbarStore.selectedFilter$, this.#manipulationsToolbarStore.page$),
            switchMap(([event, selectedFilter, page]) => this.#approvalSercive.getApprovals(event ? 1 : page, selectedFilter, this.currentSort)),
            tap((approvals: PageResult<Approval>) => this.patchState({ approvals })),
            tap(() => {
                this.patchState({ loading: false })
                this.loadApprovalsExtra();
            }),
        )
    );

    readonly loadApprovalsExtra = this.effect<void | boolean>(
        pipe(
            tap(() => this.patchState({ loadingExtra: true })),
            tap(() => this.#manipulationsToolbarStore.updatePage()),
            withLatestFrom(this.#manipulationsToolbarStore.selectedFilter$, this.#manipulationsToolbarStore.page$),
            switchMap(([_, selectedFilter, page]) => this.#approvalSercive.getApprovalsExtra(page, selectedFilter, this.currentSort)),
            tap((approvalsExtra: PageResult<Approval>) => this.patchState({ approvalsExtra })),
            tap(() => {
                this.patchState({ loadingExtra: false })
            })
        )
    );

    readonly updateApprovalWithExtra = this.updater((state) => (
        {
            ...state,
            isRequestOfExtaraWasEmpty: ((this.approvalsExtraSig$()?.result == undefined || this.approvalsExtraSig$()?.result.length == 0) && this.loadingExtraSig$()) ? true : false,
            approvals: {
                result: [...(this.approvalsSig$()?.result || []), ...(this.approvalsExtraSig$()?.result || [])],
                totals: this.approvalsSig$()?.totals || 0
            },
            approvalsExtra: { result: [], totals: 0 }
        }
    ));

    readonly updateApprovalStatus = this.effect<{
        guid: string;
        status: EApprovalStatus;
        approvedAmount?: number;
        comment?: string;
    }>((params$) =>
        params$.pipe(
            switchMap(({ guid, status, approvedAmount, comment }) =>
                this.#approvalSercive.updateApprovalStatus(guid, status, approvedAmount, comment).pipe(
                    tapResponse(
                        (id) => {
                            this.applyApprovalStatus({ id, status });
                            this.clearSenderApprovedTotals();
                            this.#globalStore.updateCountApprovals(1)
                            this.#dialog
                                .open(SendSuccessComponent, {
                                    position: { top: '8%' },
                                    panelClass: 'dialog-panel',
                                    data: { isFromApprovalResponse: true }
                                })
                                .afterClosed()
                                .subscribe((x) => {
                                    if (!x) {
                                        this.#router.navigate(['main/grid/approvals']);
                                    }
                                });
                        },
                        (error: HttpErrorResponse) => {
                            this.patchState({ error: error?.message, loading: false });
                            const message = error.error?.message || error.error || error.message;
                            this.#tostesStore.addTost({ guid: this.#tostesStore.generateGuid(), message: { en: "An error occurred while sending the response", he: "ארעה שגיאה בשליחת התגובה" }, position: TostPositionEnum.BottomLeft, seconds: 5, type: TostTypeEnum.ERROR })
                        }
                    )
                )
            )
        )
    );
    readonly applyApprovalStatus = this.updater(
        (state, payload: { id: number; status: number }) => {
            const { id, status } = payload;
            const updateList = (list: PageResult<Approval>): PageResult<Approval> => ({
                ...list,
                result: (list.result || []).map(a =>
                    a.id === id ? { ...a, status } : a
                )
            });

            return {
                ...state,
                approvals: updateList(state.approvals),
                approvalsExtra: updateList(state.approvalsExtra)
            };
        }
    );

    // Drops submissions that just moved on to the next tab (e.g. bulk-approved out of
    // PendingApproval) out of the currently-viewed list entirely, instead of leaving them behind
    // with a stale status. Also drops their now-irrelevant cached expand/select/expense-row state.
    readonly removeApprovals = this.updater((state, ids: number[]) => {
        const idSet = new Set(ids);
        const filterList = (list: PageResult<Approval>): PageResult<Approval> => {
            const removedCount = (list.result || []).filter((a) => idSet.has(a.id)).length;
            return {
                ...list,
                result: (list.result || []).filter((a) => !idSet.has(a.id)),
                totals: Math.max(0, (list.totals ?? 0) - removedCount)
            };
        };
        const omit = <T>(record: Record<number, T>): Record<number, T> =>
            Object.fromEntries(Object.entries(record).filter(([key]) => !idSet.has(Number(key))));

        return {
            ...state,
            approvals: filterList(state.approvals),
            approvalsExtra: filterList(state.approvalsExtra),
            expandedIds: omit(state.expandedIds),
            selectedIds: omit(state.selectedIds),
            expenseRowsByApprovalId: omit(state.expenseRowsByApprovalId),
            loadingExpenseRowsIds: omit(state.loadingExpenseRowsIds),
            expenseRowsErrorByApprovalId: omit(state.expenseRowsErrorByApprovalId)
        };
    });

    // Expand/collapse a submission's header; the first time it opens, lazy-load and cache its
    // expense rows (collapsing again keeps the cache, so re-opening doesn't refetch).
    readonly toggleExpand = this.effect<Approval>((approval$) =>
        approval$.pipe(
            withLatestFrom(this.state$),
            tap(([approval, state]) => {
                const isExpanding = !state.expandedIds[approval.id];
                this.patchState({ expandedIds: { ...state.expandedIds, [approval.id]: isExpanding } });
                if (isExpanding && !state.expenseRowsByApprovalId[approval.id]) {
                    if (approval.recipientGuid) {
                        this.loadExpenseRows({ approvalId: approval.id, guid: approval.recipientGuid });
                    } else {
                        this.patchState((s) => ({
                            expenseRowsErrorByApprovalId: { ...s.expenseRowsErrorByApprovalId, [approval.id]: 'Missing recipient guid' }
                        }));
                    }
                }
            })
        )
    );

    // Global expand/collapse-all: additive to the existing per-row toggleExpand, doesn't replace it.
    // If every currently-loaded submission is already expanded, collapses them all; otherwise
    // expands them all, lazy-loading expense rows for whichever ones aren't cached yet.
    readonly toggleExpandAll = this.effect<Approval[]>((approvals$) =>
        approvals$.pipe(
            withLatestFrom(this.state$),
            tap(([approvals, state]) => {
                const allExpanded = approvals.length > 0 && approvals.every((a) => !!state.expandedIds[a.id]);
                this.patchState({
                    expandedIds: allExpanded ? {} : Object.fromEntries(approvals.map((a) => [a.id, true]))
                });
                if (!allExpanded) {
                    approvals.forEach((a) => {
                        if (state.expenseRowsByApprovalId[a.id]) return;
                        if (a.recipientGuid) {
                            this.loadExpenseRows({ approvalId: a.id, guid: a.recipientGuid });
                        } else {
                            this.patchState((s) => ({
                                expenseRowsErrorByApprovalId: { ...s.expenseRowsErrorByApprovalId, [a.id]: 'Missing recipient guid' }
                            }));
                        }
                    });
                }
            })
        )
    );

    // mergeMap, not switchMap: this fires once per submission being expanded (a single click, or a
    // whole batch from toggleExpandAll). switchMap would let only the LAST call survive, silently
    // canceling every other in-flight request sharing this one effect - which was exactly why
    // "expand all"/quick successive clicks left most rows stuck with no content.
    readonly loadExpenseRows = this.effect<{ approvalId: number; guid: string }>(
        pipe(
            tap(({ approvalId }) =>
                this.patchState((state) => ({
                    loadingExpenseRowsIds: { ...state.loadingExpenseRowsIds, [approvalId]: true },
                    expenseRowsErrorByApprovalId: { ...state.expenseRowsErrorByApprovalId, [approvalId]: '' }
                }))
            ),
            mergeMap(({ approvalId, guid }) =>
                this.#approvalSercive.getExpenseRows(guid).pipe(
                    tapResponse(
                        (rows) =>
                            this.patchState((state) => ({
                                expenseRowsByApprovalId: { ...state.expenseRowsByApprovalId, [approvalId]: rows },
                                loadingExpenseRowsIds: { ...state.loadingExpenseRowsIds, [approvalId]: false }
                            })),
                        (error: HttpErrorResponse) => {
                            const message = error.error?.message || error.error || error.message || 'Unknown error';
                            this.patchState((state) => ({
                                loadingExpenseRowsIds: { ...state.loadingExpenseRowsIds, [approvalId]: false },
                                expenseRowsErrorByApprovalId: { ...state.expenseRowsErrorByApprovalId, [approvalId]: message }
                            }));
                            this.#tostesStore.addTost({
                                guid: this.#tostesStore.generateGuid(),
                                message: { en: "An error occurred while loading the expense rows", he: "ארעה שגיאה בטעינת שורות ההוצאה" },
                                position: TostPositionEnum.BottomLeft, seconds: 5, type: TostTypeEnum.ERROR
                            });
                        }
                    )
                )
            )
        )
    );

    // Fetched lazily on hover, not for every loaded row up front, since the approvals list is
    // paginated and this total spans all of the sender's matching requests, not just the ones
    // currently loaded. Keyed by senderId+filter so repeat hovers (same sender, same filter - the
    // common case when one sender has several requests) reuse the cached value instead of refetching.
    readonly fetchSenderApprovedTotal = this.effect<{ senderId: number; filterKey: string; filter: SelectedFilter | null | undefined }>(
        pipe(
            withLatestFrom(this.state$),
            filter(([{ filterKey }, state]) => !(filterKey in state.senderApprovedTotals) && !state.loadingSenderApprovedTotalKeys[filterKey]),
            tap(([{ filterKey }]) =>
                this.patchState((state) => ({
                    loadingSenderApprovedTotalKeys: { ...state.loadingSenderApprovedTotalKeys, [filterKey]: true }
                }))
            ),
            mergeMap(([{ senderId, filterKey, filter: selectedFilter }]) =>
                this.#approvalSercive.getSenderApprovedTotal(senderId, selectedFilter).pipe(
                    tapResponse(
                        (total) =>
                            this.patchState((state) => ({
                                senderApprovedTotals: { ...state.senderApprovedTotals, [filterKey]: total },
                                loadingSenderApprovedTotalKeys: { ...state.loadingSenderApprovedTotalKeys, [filterKey]: false }
                            })),
                        () =>
                            this.patchState((state) => ({
                                loadingSenderApprovedTotalKeys: { ...state.loadingSenderApprovedTotalKeys, [filterKey]: false }
                            }))
                    )
                )
            )
        )
    );

    // Clears the whole cache rather than targeting specific keys: an approval/expense change can
    // shift a sender's total across several tabs at once (e.g. approving moves a row out of the
    // Pending sum and into the Approved sum for that same sender), and the cache keys are opaque
    // "senderId:filterJson" strings that aren't cheap to selectively invalidate. Call this after any
    // mutation that can change an approval's status or a row's approved amount, so a hover right
    // after doesn't show a stale total.
    readonly clearSenderApprovedTotals = this.updater((state) => ({
        ...state,
        senderApprovedTotals: {},
        loadingSenderApprovedTotalKeys: {}
    }));

    readonly toggleSelect = this.updater((state, id: number) => ({
        ...state,
        selectedIds: { ...state.selectedIds, [id]: !state.selectedIds[id] }
    }));

    readonly clearSelection = this.updater((state) => ({ ...state, selectedIds: {} }));

    // Select-all toggle for every currently-loaded submission: if all of them are already
    // selected, clears the selection; otherwise selects every one of them.
    readonly toggleSelectAll = this.updater((state, ids: number[]) => {
        const allSelected = ids.length > 0 && ids.every((id) => !!state.selectedIds[id]);
        return { ...state, selectedIds: allSelected ? {} : Object.fromEntries(ids.map((id) => [id, true])) };
    });

    // Autosave: persists one field change on one expense row immediately (on blur/toggle), then
    // patches the cached row locally so the UI reflects it without a refetch.
    readonly updateExpenseField = this.effect<{ approvalId: number; expenseId: number; fields: UpdateExpenseApprovalFields }>(
        pipe(
            switchMap(({ approvalId, expenseId, fields }) =>
                this.#approvalSercive.updateExpenseApprovalFields(expenseId, fields).pipe(
                    tapResponse(
                        () => {
                            this.patchExpenseRowLocally({ approvalId, expenseId, fields });
                            if (fields.approvedAmount !== undefined) this.clearSenderApprovedTotals();
                        },
                        () => {
                            this.#tostesStore.addTost({
                                guid: this.#tostesStore.generateGuid(),
                                message: { en: "An error occurred while saving the change", he: "ארעה שגיאה בשמירת השינוי" },
                                position: TostPositionEnum.BottomLeft, seconds: 5, type: TostTypeEnum.ERROR
                            });
                        }
                    )
                )
            )
        )
    );

    readonly setApprovalRowValidity = this.updater(
        (state, payload: { approvalId: number; isInvalid: boolean }) => {
            const invalidApprovalIds = { ...state.invalidApprovalIds };
            if (payload.isInvalid) invalidApprovalIds[payload.approvalId] = true;
            else delete invalidApprovalIds[payload.approvalId];
            return { ...state, invalidApprovalIds };
        }
    );

    readonly patchExpenseRowLocally = this.updater(
        (state, payload: { approvalId: number; expenseId: number; fields: Partial<Expense> }) => {
            const rows = state.expenseRowsByApprovalId[payload.approvalId];
            if (!rows) return state;
            return {
                ...state,
                expenseRowsByApprovalId: {
                    ...state.expenseRowsByApprovalId,
                    [payload.approvalId]: rows.map((r) => (r.id === payload.expenseId ? { ...r, ...payload.fields } : r))
                }
            };
        }
    );

    // Bulk approve only - every selected submission is approved as one atomic server call.
    // Rejection always stays a single-submission action via the existing popup/updateApprovalStatus.
    readonly bulkApprove = this.effect<void>((trigger$) =>
        trigger$.pipe(
            withLatestFrom(this.selectedIds$, this.approvals$),
            switchMap(([, selectedIds, approvals]) => {
                const selectedApprovals = approvals.result.filter((a) => selectedIds[a.id]);
                const guids = selectedApprovals.map((a) => a.recipientGuid).filter((g): g is string => !!g);
                return this.#approvalSercive.bulkUpdateApprovalStatus(guids).pipe(
                    tapResponse(
                        (results) => {
                            const approvedIds = results
                                .filter((r) => r.success)
                                .map((r) => selectedApprovals.find((a) => a.recipientGuid === r.guid)?.id)
                                .filter((id): id is number => id != null);
                            this.removeApprovals(approvedIds);
                            this.clearSenderApprovedTotals();
                            const successCount = results.filter((r) => r.success).length;
                            const failCount = results.length - successCount;
                            this.clearSelection();
                            this.#tostesStore.addTost({
                                guid: this.#tostesStore.generateGuid(),
                                message: failCount === 0
                                    ? { en: `${successCount} requests approved successfully`, he: `${successCount} שאלונים אושרו בהצלחה` }
                                    : { en: `${successCount} approved, ${failCount} failed`, he: `${successCount} שאלונים אושרו, ${failCount} נכשלו` },
                                position: TostPositionEnum.BottomLeft, seconds: 5,
                                type: failCount === 0 ? TostTypeEnum.SUCCESS : TostTypeEnum.ERROR
                            });
                        },
                        (error: HttpErrorResponse) => {
                            const message = error.error?.message || error.error || error.message;
                            this.#tostesStore.addTost({
                                guid: this.#tostesStore.generateGuid(),
                                message: { en: "An error occurred while approving the selected requests", he: "ארעה שגיאה באישור השאלונים הנבחרים" },
                                position: TostPositionEnum.BottomLeft, seconds: 5, type: TostTypeEnum.ERROR
                            });
                        }
                    )
                );
            })
        )
    );
}
